import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import DOMPurify from 'dompurify';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BodyM, BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import {
  BLOG_SPORT,
  BLOG_SPORT_CATEGORY_CATEGORY,
  BLOG_SPORT_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';

import { DEFAULT_POST_PICTURE } from 'constants/types';
import { useDisplayCategories } from 'hooks/useDisplayCategories';
import { usePostImage } from 'hooks/usePostImage';
import { getPostCategory } from 'lib/postCategory';

import type { PostExcerpt } from './__generated__/index.graphql';

type Props = {
  excerpt: PostExcerpt;
  featured: boolean;
};

const Container = styled(LinkBox)`
  position: relative;

  & {
    text-decoration: none !important;
  }

  & figure {
    img {
      width: 100%;
      border-radius: var(--unit);
    }
  }
`;

const Subtitle = styled(LabelM)`
  color: var(--c-nd-600);
`;

export const Excerpt = ({ excerpt, featured }: Props) => {
  const postImage = usePostImage(excerpt, featured);
  const sportCategory = getPostCategory(excerpt);

  const { categoryDisplayName } = useDisplayCategories({
    categories: excerpt.categories,
  });

  const { formatDistanceToNow } = useIntlContext();
  const timeAgo = excerpt.date
    ? formatDistanceToNow(excerpt.date, { addSuffix: true })
    : '';

  const excerptSport = excerpt?.categories?.nodes[0].name;

  return (
    <Container>
      <LinkOverlay
        as={Link}
        to={generatePath(BLOG_SPORT_SLUG, {
          sport: sportCategory,
          slug: excerpt.slug,
        })}
      >
        {!!postImage && (
          <figure>
            <img
              src={postImage.url || DEFAULT_POST_PICTURE}
              alt={postImage.alt || excerpt.title || ''}
            />
          </figure>
        )}
      </LinkOverlay>
      <Vertical>
        <LinkOther
          as={Link}
          to={generatePath(BLOG_SPORT_CATEGORY_CATEGORY, {
            sport: excerptSport?.toLowerCase() ?? '',
            category: categoryDisplayName.toLowerCase(),
          })}
        >
          <Chip color="transparent" size="medium">
            {categoryDisplayName}
          </Chip>
        </LinkOther>
        <BrandM as="h4">{excerpt.title}</BrandM>
        <LinkOther
          as={Link}
          to={generatePath(BLOG_SPORT, {
            sport: excerptSport?.toLowerCase() ?? '',
          })}
        >
          <Subtitle>
            {excerptSport} - {timeAgo}
          </Subtitle>
        </LinkOther>
        <BodyM
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(excerpt.excerpt!),
          }}
        />
      </Vertical>
    </Container>
  );
};

Excerpt.fragment = gql`
  fragment PostExcerpt on Post {
    databaseId
    title
    slug
    excerpt
    content
    modified
    date
    categories {
      nodes {
        name
      }
    }
    featured {
      featured
    }
    featuredImage {
      node {
        altText
        thumbnail: sourceUrl(size: MEDIUM)
        sourceUrl: sourceUrl(size: LARGE)
      }
    }
    ...getPostCategory_Post
  }
  ${getPostCategory.fragments.post}
` as TypedDocumentNode<PostExcerpt>;
